// The four actions (rules p3: "There are 4 possible actions ... you must
// choose and perform exactly 1 of them"), plus the leg and end scoring they
// trigger. Pure reducer: dispatch(state, action) -> newState.

import { nextInt } from '../rng/rng';
import {
  moveCamel, rankCamels, raceWon, legalDesertSpaces, spaceOfCamel,
} from '../moves';
import {
  CAMELS, DIE_MAX, PHASES, SECOND_PLACE_PAYOUT, WRONG_LEG_TILE_PENALTY,
  PYRAMID_TILE_PAYOUT, DESERT_TRIGGER_PAYOUT, FINAL_BET_PAYOUTS,
  FINAL_BET_CONSOLATION, WRONG_FINAL_BET_PENALTY, TRACK_LENGTH,
} from '../constants';

export const takeLegTileAction = (camel) => ({ type: 'TAKE_LEG_TILE', camel });
export const placeDesertAction = (space, side) => ({ type: 'PLACE_DESERT', space, side });
export const rollAction = () => ({ type: 'ROLL' });
export const betAction = (pile, camel) => ({ type: 'BET', pile, camel });

const clone = (state) => JSON.parse(JSON.stringify(state));

// "No player can drop below 0 EP" (p2) - applied per payment, so a player
// at 0 is spared each individual loss.
const pay = (player, delta) => {
  player.money = Math.max(0, player.money + delta);
};

const advanceTurn = (state) => {
  state.turn.activeSeat = (state.turn.activeSeat + 1) % state.players.length;
};

const bump = (state) => { state.meta.seq += 1; };

// ---- leg scoring (rules p4) ------------------------------------------------

function scoreLeg(state) {
  const ranked = rankCamels(state.track);
  const leader = ranked[0];
  const second = ranked[1];

  const results = state.players.map((player) => {
    const lines = [];
    let delta = 0;
    player.legTiles.forEach((tile) => {
      let gain;
      if (tile.camel === leader) gain = tile.value;
      else if (tile.camel === second) gain = SECOND_PLACE_PAYOUT;
      else gain = -WRONG_LEG_TILE_PENALTY;
      const before = player.money;
      pay(player, gain);
      delta += player.money - before;
      lines.push({ kind: 'legTile', camel: tile.camel, value: tile.value, gain });
    });
    if (player.pyramidTiles > 0) {
      const gain = player.pyramidTiles * PYRAMID_TILE_PAYOUT;
      player.money += gain;
      delta += gain;
      lines.push({ kind: 'pyramid', count: player.pyramidTiles, gain });
    }
    return { seat: player.seat, name: player.name, delta, lines, money: player.money };
  });

  state.legReport = {
    leg: state.turn.leg,
    leader,
    second,
    results,
  };

  // Reset for the next leg (p4): tiles back, desert tiles back to their
  // owners, dice back into the pyramid.
  state.players.forEach((p) => { p.legTiles = []; p.pyramidTiles = 0; });
  CAMELS.forEach((camel) => { state.legTiles[camel] = [5, 3, 2]; });
  state.desert = {};
  state.legDice = CAMELS.slice();
  state.diceRolled = [];
  state.turn.leg += 1;
  // The marker goes "to the player that sits to the left of the player who
  // just took the last Pyramid tile" (p4) - which is exactly whose turn is
  // next anyway. scoreLeg runs while activeSeat is still the roller, so the
  // marker is stamped by dispatch AFTER the turn advances (see the flag).
  state.turn.markLegStart = true;
}

// ---- end scoring (rules p4) ------------------------------------------------

function scoreFinal(state) {
  const ranked = rankCamels(state.track);
  const winner = ranked[0];
  const loser = ranked[ranked.length - 1];
  state.meta.winnerCamel = winner;
  state.meta.loserCamel = loser;

  const piles = [];
  for (const [pile, target] of [['winner', winner], ['loser', loser]]) {
    let correctSoFar = 0;
    const cards = state.bets[pile].map((bet) => {
      const player = state.players[bet.seat];
      let gain;
      if (bet.camel === target) {
        gain = FINAL_BET_PAYOUTS[correctSoFar] ?? FINAL_BET_CONSOLATION;
        correctSoFar += 1;
        player.money += gain;
      } else {
        const before = player.money;
        pay(player, -WRONG_FINAL_BET_PENALTY);
        gain = player.money - before;
      }
      return { seat: bet.seat, name: state.players[bet.seat].name, camel: bet.camel, gain };
    });
    piles.push({ pile, target, cards });
  }

  state.meta.finalReport = {
    winner,
    loser,
    piles,
    standings: [...state.players]
      .map((p) => ({ seat: p.seat, name: p.name, money: p.money }))
      .sort((a, b) => b.money - a.money),
  };
  state.meta.phase = PHASES.FINISHED;
}

// ---- the four actions ------------------------------------------------------

function applyTakeLegTile(state, action) {
  const pile = state.legTiles[action.camel];
  if (!pile || pile.length === 0) return null;
  const next = clone(state);
  const value = next.legTiles[action.camel].shift(); // the 5 sits on top (p2)
  const player = next.players[next.turn.activeSeat];
  player.legTiles.push({ camel: action.camel, value });
  next.log.push({ kind: 'legTile', seat: player.seat, camel: action.camel, value });
  return next;
}

function applyPlaceDesert(state, action) {
  const seat = state.turn.activeSeat;
  const legal = legalDesertSpaces(state.track, state.desert, seat);
  if (!legal.includes(action.space)) return null;
  if (!['oasis', 'mirage'].includes(action.side)) return null;
  const current = state.desert[seat];
  if (current && current.space === action.space && current.side === action.side) return null;
  const next = clone(state);
  next.desert[seat] = { space: action.space, side: action.side };
  next.log.push({ kind: 'desert', seat, space: action.space, side: action.side });
  return next;
}

function applyRoll(state) {
  if (state.legDice.length === 0) return null;
  const next = clone(state);
  const player = next.players[next.turn.activeSeat];

  let dieIndex; let faceRoll;
  [dieIndex, next.rngState] = nextInt(next.legDice.length, next.rngState);
  [faceRoll, next.rngState] = nextInt(DIE_MAX, next.rngState);
  const camel = next.legDice.splice(dieIndex, 1)[0];
  const value = faceRoll + 1;

  const { track, desertSeat } = moveCamel(next.track, camel, value, next.desert);
  next.track = track;
  if (desertSeat !== null) {
    next.players[desertSeat].money += DESERT_TRIGGER_PAYOUT; // p3, at once
  }

  player.pyramidTiles += 1; // pays out at leg scoring (p3)
  next.diceRolled.push({ camel, value });
  next.lastRoll = { camel, value, desertSeat };
  next.log.push({ kind: 'roll', seat: player.seat, camel, value });

  // "The game ends as soon as the first Camel crosses the finish line" (p2):
  // the race ends immediately, the leg is scored one last time, then the
  // end scoring round (p4).
  if (raceWon(next.track)) {
    scoreLeg(next);
    scoreFinal(next);
    bump(next);
    return next;
  }

  if (next.legDice.length === 0) scoreLeg(next);
  return next;
}

function applyBet(state, action) {
  if (!['winner', 'loser'].includes(action.pile)) return null;
  if (!CAMELS.includes(action.camel)) return null;
  const player = state.players[state.turn.activeSeat];
  if (!player.betCards[action.camel]) return null;
  const next = clone(state);
  next.players[next.turn.activeSeat].betCards[action.camel] = false;
  next.bets[action.pile].push({ seat: player.seat, camel: action.camel });
  // The camel stays face down; the log says only WHERE the card went.
  next.log.push({ kind: 'bet', seat: player.seat, pile: action.pile });
  return next;
}

export function dispatch(state, action) {
  if (state.meta.phase !== PHASES.PLAYING) return state;
  let next = null;
  switch (action?.type) {
    case 'TAKE_LEG_TILE': next = applyTakeLegTile(state, action); break;
    case 'PLACE_DESERT': next = applyPlaceDesert(state, action); break;
    case 'ROLL': next = applyRoll(state); break;
    case 'BET': next = applyBet(state, action); break;
    default: return state;
  }
  if (!next) return state;
  if (next.meta.phase === PHASES.PLAYING) {
    advanceTurn(next);
    if (next.turn.markLegStart) {
      next.turn.legStartSeat = next.turn.activeSeat;
      delete next.turn.markLegStart;
    }
    bump(next);
  }
  return next;
}

export { rankCamels, legalDesertSpaces, spaceOfCamel, TRACK_LENGTH };
